import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { fetchStartup, fetchStartupAccess, fetchStartupDocuments, downloadStartupDocument, startupCheckout } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, ShieldCheck, Layers, Boxes, Loader2,
  Download, FileText, Rocket, DollarSign, Sparkles, ExternalLink, HelpCircle,
} from "lucide-react";
import { HowItWorksModal } from "../components/HowItWorksModal";

const JOURNEY = ["Landing", "Sign Up", "Onboarding", "Dashboard", "Core Features", "Settings"];

export default function AppDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, requireAuth } = useAuth();
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState({ purchased: false });
  const [docs, setDocs] = useState([]);
  const [buying, setBuying] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [howOpen, setHowOpen] = useState(false);

  const load = async () => {
    try {
      const s = await fetchStartup(id);
      setSt(s);
    } catch { toast.error("Startup not found"); navigate("/apps"); return; }
    finally { setLoading(false); }
    if (user) {
      try {
        const a = await fetchStartupAccess(id);
        setAccess(a);
        if (a.purchased) setDocs(await fetchStartupDocuments(id));
      } catch { /* not purchased */ }
    }
    setAccessLoaded(true);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user]);

  useEffect(() => {
    if (params.get("continue") === "1" && st && accessLoaded && !access.purchased) continueBuilding();
    // eslint-disable-next-line
  }, [st, accessLoaded]);

  const continueBuilding = async () => {
    if (!user) { requireAuth(() => {}); return; }
    if (access.purchased) { toast.success("You already own this startup"); return; }
    setBuying(true);
    try {
      const { url } = await startupCheckout(id);
      window.location.href = url;
    } catch { toast.error("Could not start checkout. Please try again."); setBuying(false); }
  };

  const download = async (doc) => {
    try { await downloadStartupDocument(id, doc); }
    catch { toast.error("Download failed."); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-[#687280]"><Loader2 className="animate-spin mr-2" /> Loading…</div></Layout>;
  if (!st) return null;
  const g = st.gradient || ["#D4AF37", "#0F0F0F"];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <button onClick={() => navigate("/apps")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#687280] hover:text-[#0F0F0F] mb-6" data-testid="startup-back-btn"><ArrowLeft size={16} /> Back to Startups</button>

        {/* Hero */}
        <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm">
          <div className="h-48 flex items-center justify-center relative" style={{ background: `linear-gradient(135deg, ${g[0]} 0%, ${g[1]} 100%)` }}>
            <span className="absolute top-4 left-4 text-[11px] font-bold bg-white/90 text-[#0F0F0F] px-3 py-1 rounded-full">Ready-to-Build Startup</span>
            {st.preview_url && (
              <a href={st.preview_url} target="_blank" rel="noopener noreferrer" className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-xs font-bold bg-[#0F0F0F]/85 text-white px-3 py-1.5 rounded-full hover:bg-[#0F0F0F] transition-colors backdrop-blur-sm" data-testid="startup-openlive-btn">
                <ExternalLink size={13} /> Open Live Preview
              </a>
            )}
            <span className="font-display text-4xl font-extrabold text-white drop-shadow">{st.name}</span>
          </div>
          <div className="p-6 md:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wide">{st.category} · {st.platform}</p>
                <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F0F0F] mt-1">{st.name}</h1>
                <p className="text-[#687280] mt-2 text-base leading-relaxed">{st.tagline}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(st.tech || []).map((t) => <span key={t} className="text-[11px] font-semibold bg-[#F7F7F7] text-[#374151] px-2.5 py-1 rounded-md border border-gray-100">{t}</span>)}
                </div>
                <div className="flex items-center gap-4 mt-4 text-sm text-[#687280]">
                  <span className="flex items-center gap-1.5"><Layers size={15} className="text-[#D4AF37]" /> {st.screens_count} Screens</span>
                  <span className="flex items-center gap-1.5"><Boxes size={15} className="text-[#D4AF37]" /> {st.features_count} Features</span>
                  <span className="flex items-center gap-1.5 font-semibold text-[#0F0F0F]"><ShieldCheck size={15} className="text-[#B8901F]" /> Production-Ready Build</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-[#687280]">Fixed price</p>
                <p className="font-display text-3xl font-extrabold text-[#0F0F0F]">$250</p>
                <button onClick={continueBuilding} disabled={buying} className="mt-3 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-7 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="continue-building-btn">
                  {buying ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                  {access.purchased ? "Owned" : "Continue Building"}
                </button>
                <div className="mt-2.5 flex md:justify-end">
                  <button onClick={() => setHowOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] transition-colors" data-testid="how-it-works-detail-btn">
                    <HelpCircle size={14} className="text-[#D4AF37]" /> How It Works
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Purchased: downloads */}
        {access.purchased && (
          <div className="mt-6 rounded-2xl border-2 border-[#D4AF37] bg-[#FBF7EA] p-6" data-testid="startup-downloads">
            <div className="flex items-center gap-2"><Check size={18} className="text-[#B8901F]" /><h2 className="font-display text-xl font-bold text-[#0F0F0F]">Your Startup Files</h2></div>
            <p className="text-sm text-[#8a6d1a] mt-1">Download these files and upload them into your AI builder to continue building.</p>
            {docs.length === 0 ? (
              <p className="text-sm text-[#687280] mt-4">Files are being prepared. Your download will appear here shortly — you'll also receive an email within 24 hours.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-3">
                    <div className="flex items-center gap-2 min-w-0"><FileText size={16} className="text-[#D4AF37] shrink-0" /><span className="text-sm text-[#0F0F0F] truncate">{d.filename}</span></div>
                    <button onClick={() => download(d)} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0F0F0F] text-white rounded-full px-3 py-1.5 hover:bg-[#1c1c1c]" data-testid={`download-doc-${d.id}`}><Download size={13} /> Download</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 space-y-6">
            <Section title="Startup Overview">
              <Field label="Problem" value={st.problem} />
              <Field label="Solution" value={st.solution} />
              <Field label="Target Audience" value={st.target_audience} />
              <Field label="Value Proposition" value={st.value_prop} />
            </Section>

            {st.features?.length > 0 && (
              <Section title="Feature Overview">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {st.features.map((f) => <div key={f} className="flex gap-2 text-sm text-[#374151]"><Check size={15} className="mt-0.5 shrink-0 text-[#D4AF37]" />{f}</div>)}
                </div>
              </Section>
            )}

            <Section title="User Journey">
              <div className="flex flex-wrap items-center gap-2">
                {JOURNEY.map((j, i) => (
                  <div key={j} className="flex items-center gap-2">
                    <span className="text-sm font-medium bg-[#F7F7F7] border border-gray-100 rounded-full px-3 py-1.5 text-[#374151]">{j}</span>
                    {i < JOURNEY.length - 1 && <ArrowRight size={14} className="text-[#D4AF37]" />}
                  </div>
                ))}
              </div>
            </Section>
          </div>

          <div className="space-y-6">
            {st.tech?.length > 0 && (
              <Section title="Technology Stack">
                <div className="flex flex-wrap gap-1.5">
                  {st.tech.map((t) => <span key={t} className="text-xs font-semibold bg-[#F7F7F7] text-[#374151] px-2.5 py-1 rounded-md border border-gray-100">{t}</span>)}
                </div>
              </Section>
            )}
            {st.monetization?.length > 0 && (
              <Section title="Monetization Options">
                <div className="space-y-1.5">
                  {st.monetization.map((m) => <div key={m} className="flex gap-2 text-sm text-[#374151]"><DollarSign size={14} className="mt-0.5 shrink-0 text-[#D4AF37]" />{m}</div>)}
                </div>
              </Section>
            )}
            {st.roadmap?.length > 0 && (
              <Section title="Future Roadmap">
                <div className="space-y-1.5">
                  {st.roadmap.map((r) => <div key={r} className="flex gap-2 text-sm text-[#374151]"><Sparkles size={14} className="mt-0.5 shrink-0 text-[#D4AF37]" />{r}</div>)}
                </div>
              </Section>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-8 text-center">
          <h2 className="font-display text-2xl font-extrabold">Make {st.name} your own</h2>
          <p className="text-[#c9c39f] mt-2 max-w-xl mx-auto">Purchase this startup foundation for $250. After payment, download the project files and transfer them into your AI builder to continue building.</p>
          <button onClick={continueBuilding} disabled={buying} className="mt-5 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="continue-building-btn-bottom">
            {buying ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} {access.purchased ? "You own this startup" : "Continue Building · $250"}
          </button>
        </div>
      </div>

      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </Layout>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
    <h2 className="font-display text-lg font-bold text-[#0F0F0F] mb-3">{title}</h2>
    {children}
  </div>
);
const Field = ({ label, value }) => value ? (
  <div className="mb-3 last:mb-0">
    <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">{label}</p>
    <p className="text-sm text-[#374151] mt-0.5 leading-relaxed">{value}</p>
  </div>
) : null;
